/**
 * CurrencyConfigSection — Phase 36 Batch X (GMIA wizard "Step 1.5").
 *
 * Lives inside the Property step (Step 1 colloquially / step==2 in the
 * indexed wizard). Rendered conditionally:
 *
 *   • property_currency == base_currency  → tidy compact pill, no inputs.
 *   • property_currency != base_currency  → cross-currency card with
 *     a "Did you already exchange funds?" toggle. YES path collects the
 *     locked rate + an optional purchase date. NO path is the default —
 *     the server locks today's live rate at create.
 *
 * Reads:
 *   wizard.form.property.country      — drives property_currency lookup
 *   wizard.countries                  — { code, name, currency, … } list
 *   wizard.user.home_currency         — base currency (fallback chain)
 *   wizard.form.analysis.did_exchange_funds
 *   wizard.form.analysis.purchase_exchange_rate
 *   wizard.form.analysis.purchase_date
 *
 * Writes:
 *   wizard.setAnalysis("did_exchange_funds", …)
 *   wizard.setAnalysis("purchase_exchange_rate", …)
 *   wizard.setAnalysis("purchase_date", …)
 *
 * Same-currency case writes nothing; backend's `compute_fx_adjusted_view`
 * collapses to a neutral pass-through and the Deal Detail FX panel hides
 * itself entirely (fx_view.fx_applicable === false).
 */
import { ArrowRightLeft, Coins, Check, Info } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const FLAG_BY_CCY = {
  USD: "🇺🇸", EUR: "🇪🇺", GBP: "🇬🇧", JPY: "🇯🇵", CAD: "🇨🇦",
  AUD: "🇦🇺", CHF: "🇨🇭", CNY: "🇨🇳", MXN: "🇲🇽", BRL: "🇧🇷",
  COP: "🇨🇴", ARS: "🇦🇷", CLP: "🇨🇱", PEN: "🇵🇪", DOP: "🇩🇴",
  INR: "🇮🇳", ZAR: "🇿🇦", SGD: "🇸🇬", HKD: "🇭🇰", SEK: "🇸🇪",
  NOK: "🇳🇴", DKK: "🇩🇰", PLN: "🇵🇱", CZK: "🇨🇿", HUF: "🇭🇺",
  AED: "🇦🇪", SAR: "🇸🇦", TRY: "🇹🇷", ILS: "🇮🇱", THB: "🇹🇭",
  VND: "🇻🇳", PHP: "🇵🇭", IDR: "🇮🇩", MYR: "🇲🇾", KRW: "🇰🇷",
};

// Mirror of backend/locations.py:_COUNTRY_CURRENCY. The wizard's
// CountryPicker is sourced from /api/tax-rules which only carries a
// dozen countries — but the global location picker
// (/api/locations/countries) exposes 240+. We mirror the backend's
// canonical country→currency map locally so the FX section always
// resolves the right currency regardless of which list rendered the
// country. Keep in sync with locations.py.
const COUNTRY_TO_CURRENCY = {
  US: "USD", CA: "CAD", MX: "MXN", GB: "GBP", IE: "EUR",
  AU: "AUD", NZ: "NZD", JP: "JPY", KR: "KRW", CN: "CNY",
  HK: "HKD", SG: "SGD", TW: "TWD", TH: "THB", VN: "VND",
  PH: "PHP", ID: "IDR", MY: "MYR", IN: "INR", PK: "PKR",
  BD: "BDT", LK: "LKR", NP: "NPR", AE: "AED", SA: "SAR",
  QA: "QAR", KW: "KWD", BH: "BHD", OM: "OMR", IL: "ILS",
  TR: "TRY", EG: "EGP", ZA: "ZAR", NG: "NGN", KE: "KES",
  GH: "GHS", MA: "MAD", DZ: "DZD", TN: "TND",
  DE: "EUR", FR: "EUR", IT: "EUR", ES: "EUR", PT: "EUR",
  NL: "EUR", BE: "EUR", AT: "EUR", FI: "EUR", GR: "EUR",
  CY: "EUR", MT: "EUR", LU: "EUR", SK: "EUR", SI: "EUR",
  EE: "EUR", LV: "EUR", LT: "EUR", HR: "EUR",
  CH: "CHF", SE: "SEK", NO: "NOK", DK: "DKK", IS: "ISK",
  PL: "PLN", CZ: "CZK", HU: "HUF", RO: "RON", BG: "BGN",
  RS: "RSD", UA: "UAH", RU: "RUB", BY: "BYN", GE: "GEL",
  AM: "AMD", AZ: "AZN", KZ: "KZT", UZ: "UZS",
  BR: "BRL", AR: "ARS", CL: "CLP", CO: "COP", PE: "PEN",
  UY: "UYU", PY: "PYG", BO: "BOB", VE: "VES", EC: "USD",
  PA: "USD", CR: "CRC", GT: "GTQ", HN: "HNL", NI: "NIO",
  SV: "USD", DO: "DOP", CU: "CUP", JM: "JMD", TT: "TTD",
  BB: "BBD", BS: "BSD",
};

function flag(ccy) {
  return FLAG_BY_CCY[(ccy || "").toUpperCase()] || "🌐";
}

function CurrencyChip({ ccy, label }) {
  if (!ccy) return null;
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-sm font-medium text-slate-900 shadow-sm">
      <span aria-hidden="true">{flag(ccy)}</span>
      <span className="font-semibold">{ccy}</span>
      {label ? <span className="text-xs font-normal text-slate-500">{label}</span> : null}
    </div>
  );
}

export default function CurrencyConfigSection({ wizard }) {
  const { form, setAnalysis, countries, user } = wizard;
  const country = (form.property.country || "US").toUpperCase();
  const countryMeta = (countries || []).find((c) => c.code === country);
  // Resolution order: tax-rules list (richest) → static country→currency
  // map (mirrors backend/locations.py). Either path keeps the section
  // accurate without an extra HTTP round-trip.
  const propertyCurrency =
    countryMeta?.currency || COUNTRY_TO_CURRENCY[country] || null;
  const baseCurrency =
    (user?.home_currency || user?.display_currency || "USD").toUpperCase();

  // We need both currencies known to render anything meaningful.
  if (!propertyCurrency || !baseCurrency) return null;

  const sameCurrency = propertyCurrency === baseCurrency;

  // ─── Same-currency case ─────────────────────────────────────────
  if (sameCurrency) {
    return (
      <div
        data-testid="wizard-currency-section"
        data-state="same-currency"
        className="rounded-xl border border-emerald-100 bg-emerald-50/60 px-4 py-3"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <Check className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-emerald-900">
              Same currency — no FX adjustment needed
            </p>
            <p className="mt-0.5 text-xs text-emerald-800/80">
              Property is priced in {propertyCurrency} and your base currency is {baseCurrency}.
            </p>
          </div>
          <CurrencyChip ccy={propertyCurrency} />
        </div>
      </div>
    );
  }

  // ─── Cross-currency case ────────────────────────────────────────
  const a = form.analysis;
  const didExchange = !!a.did_exchange_funds;

  return (
    <div
      data-testid="wizard-currency-section"
      data-state="cross-currency"
      className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-white px-5 py-4 shadow-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-indigo-600 text-white">
            <Coins className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              Currency configuration
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              This deal crosses borders — we'll track FX impact on returns.
            </p>
          </div>
        </div>
        <div
          className="flex items-center gap-2"
          data-testid="wizard-currency-chips"
        >
          <CurrencyChip ccy={propertyCurrency} label="Property" />
          <ArrowRightLeft className="h-4 w-4 text-slate-400" aria-hidden="true" />
          <CurrencyChip ccy={baseCurrency} label="Base" />
        </div>
      </div>

      {/* YES/NO toggle */}
      <div
        className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2"
        role="radiogroup"
        aria-label="Have you already exchanged funds?"
      >
        <button
          type="button"
          role="radio"
          aria-checked={!didExchange}
          onClick={() => setAnalysis("did_exchange_funds", false)}
          data-testid="wizard-fx-exchanged-no"
          data-active={!didExchange}
          className={`group flex items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition-all ${
            !didExchange
              ? "border-indigo-600 bg-white shadow-sm ring-2 ring-indigo-100"
              : "border-slate-200 bg-white/60 hover:border-slate-300"
          }`}
        >
          <span
            className={`mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full border-2 ${
              !didExchange ? "border-indigo-600 bg-indigo-600" : "border-slate-300 bg-white"
            }`}
          >
            {!didExchange ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-slate-900">
              I haven't exchanged yet
            </span>
            <span className="mt-0.5 block text-xs text-slate-500">
              Lock today's live rate when I analyze the deal.
            </span>
          </span>
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={didExchange}
          onClick={() => setAnalysis("did_exchange_funds", true)}
          data-testid="wizard-fx-exchanged-yes"
          data-active={didExchange}
          className={`group flex items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition-all ${
            didExchange
              ? "border-indigo-600 bg-white shadow-sm ring-2 ring-indigo-100"
              : "border-slate-200 bg-white/60 hover:border-slate-300"
          }`}
        >
          <span
            className={`mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full border-2 ${
              didExchange ? "border-indigo-600 bg-indigo-600" : "border-slate-300 bg-white"
            }`}
          >
            {didExchange ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-slate-900">
              I've already exchanged
            </span>
            <span className="mt-0.5 block text-xs text-slate-500">
              I'll enter the rate I actually got.
            </span>
          </span>
        </button>
      </div>

      {/* YES path inputs */}
      {didExchange ? (
        <div
          className="mt-4 grid grid-cols-1 gap-4 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-2"
          data-testid="wizard-fx-yes-fields"
        >
          <div>
            <Label htmlFor="wizard-fx-rate" className="text-slate-700">
              Exchange rate
            </Label>
            <div className="relative mt-1.5">
              <Input
                id="wizard-fx-rate"
                data-testid="wizard-fx-rate-input"
                type="number"
                inputMode="decimal"
                step="any"
                min="0"
                placeholder="0"
                value={a.purchase_exchange_rate === 0 ? "" : a.purchase_exchange_rate}
                onChange={(e) =>
                  setAnalysis(
                    "purchase_exchange_rate",
                    e.target.value === "" ? 0 : Number(e.target.value),
                  )
                }
                onBlur={(e) =>
                  setAnalysis(
                    "purchase_exchange_rate",
                    e.target.value === "" ? 0 : Number(e.target.value),
                  )
                }
                className="pr-20"
              />
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs font-medium text-slate-400">
                {propertyCurrency} / 1 {baseCurrency}
              </span>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              How many {propertyCurrency} you got for 1 {baseCurrency} at the time.
            </p>
          </div>
          <div>
            <Label htmlFor="wizard-fx-date" className="text-slate-700">
              Exchange date <span className="text-slate-400">(optional)</span>
            </Label>
            <Input
              id="wizard-fx-date"
              data-testid="wizard-fx-date-input"
              type="date"
              value={a.purchase_date || ""}
              onChange={(e) => setAnalysis("purchase_date", e.target.value)}
              className="mt-1.5"
            />
            <p className="mt-1 text-xs text-slate-500">
              We'll label the locked rate with this date in reports.
            </p>
          </div>
        </div>
      ) : (
        <div
          className="mt-3 flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600"
          data-testid="wizard-fx-no-hint"
        >
          <Info className="mt-0.5 h-3.5 w-3.5 flex-none text-slate-400" />
          <span>
            We'll fetch the current {propertyCurrency} → {baseCurrency} rate
            from <span className="font-semibold">open.er-api.com</span> and
            lock it on the deal. You can edit the deal later if you actually
            exchange at a different rate.
          </span>
        </div>
      )}
    </div>
  );
}
